import {
  FormControlLabel,
  Grid,
  makeStyles,
  Switch,
  Typography,
} from '@material-ui/core';
import NotificationsActiveIcon from '@material-ui/icons/NotificationsActive';
import { Alert } from '@material-ui/lab';
import clsx from 'clsx';
import ButtonGroup, { ButtonGroupItem } from 'components/ButtonGroup';
import DialogAlert, {
  DialogAlertInitial,
  DialogAlertProps,
} from 'components/DialogAlert';
import PageTitle from 'components/PageTitle';
import RefreshButton from 'components/RefreshButton';
import i18n from 'i18n';
import React from 'react';
import ApplicationState from 'store/interfaces';
import { connect } from 'react-redux';
import { CompanyData, RolesEnum } from 'modules/authenticate/interfaces';
import { RoleProps } from 'modules/permission/interfaces';
import { mapPermissionName } from 'modules/permission/helpers';
import { SearchBar } from 'components/SearchBar';
import SkeletonTemplate from './Skeleton';
import PermissionsTabs, { TabProps } from './PermissionsTabs';
import styles from './styles';

const useStyles = makeStyles(styles);

interface Props {
  title: string;
  subtitle: React.ReactNode;
  contentTitle: string;
  contentSubtitle: string;
  children: React.ReactNode;
  alert?: DialogAlertProps | null;
  generalActions?: ButtonGroupItem[];
  tabs?: {
    items: TabProps[];
    defaultValue?: number;
    onChange?: (value: number) => void;
  };
  disableArea?: boolean;
  selectAll?: {
    value: boolean;
    label: string;
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  };
  marginSearchTop?: boolean;
  slug?: 'COMPANIES' | 'PARTNERS' | 'ENVIRONMENTS' | 'CLIENTS' | 'OPERATORS';
  companySelected: CompanyData | null;
  role?: RolesEnum;
  roleInfo: RoleProps;
  isLoading: boolean;
  onRefresh?: () => void;
  onSearch?: (search?: string | undefined) => void;
}

const TemplateGroups = ({
  title,
  subtitle,
  contentTitle,
  contentSubtitle,
  generalActions,
  children,
  alert,
  tabs,
  disableArea,
  selectAll,
  marginSearchTop,
  slug,
  role,
  roleInfo,
  companySelected,
  isLoading,
  onRefresh,
  onSearch,
}: Props) => {
  const classes = useStyles();
  const [alertData, setAlertData] = React.useState<DialogAlertProps>(
    DialogAlertInitial
  );
  const [searchValue, setSearchValue] = React.useState('');

  const defaultAlertProps = {
    icon: <NotificationsActiveIcon className={classes.iconFormat} />,
    onClose: () => setAlertData(DialogAlertInitial),
  };

  React.useEffect(() => {
    const newAlertData = alert || DialogAlertInitial;

    setAlertData({
      ...defaultAlertProps,
      ...newAlertData,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alert]);

  return (
    <React.Fragment>
      <Grid
        container
        className={clsx({
          [classes.disableArea]: disableArea,
        })}
      >
        <Grid
          container
          direction="row"
          alignItems="center"
          justify="space-between"
        >
          <Grid
            container
            direction="column"
            alignItems="flex-start"
            style={{
              gap: '8px',
            }}
          >
            <PageTitle title={title} subtitle={subtitle} />
            <Grid item className={classes.template}>
              <Typography>{`${i18n.t('TEMPLATE_KEY')}: `}</Typography>
              <Grid className={classes.status}>
                <Typography>
                  {mapPermissionName(roleInfo?.from_template)}
                </Typography>
              </Grid>
            </Grid>
          </Grid>

          <Grid
            container
            direction="row"
            alignItems="center"
            justify="space-between"
            style={{ marginTop: 24 }}
          >
            <Grid item>
              <Typography className={classes.contentTitle}>
                {contentTitle}
              </Typography>
              <Typography className={classes.contentSubtitle}>
                {contentSubtitle}
              </Typography>
            </Grid>
            <Grid
              container
              xs={3}
              direction="row"
              alignItems="center"
              justify="flex-end"
            >
              {onRefresh && (
                <RefreshButton
                  id="btn-refresh"
                  onClick={onRefresh}
                  animate={isLoading}
                  disabled={isLoading}
                />
              )}

              {generalActions && (
                <ButtonGroup
                  style={{ marginLeft: 16 }}
                  label={i18n.t('GENERAL_ACTIONS_KEY')}
                  options={generalActions}
                />
              )}
            </Grid>
          </Grid>
        </Grid>

        {disableArea ? (
          <SkeletonTemplate />
        ) : (
          <>
            {tabs?.items && (
              <PermissionsTabs
                role={role}
                companySelected={companySelected}
                {...tabs}
              />
            )}
            {selectAll && (
              <Grid item xs={12} className={classes.selectAll}>
                <FormControlLabel
                  control={
                    <Switch
                      id="select-all"
                      checked={selectAll.value}
                      onChange={selectAll.onChange}
                      size="small"
                    />
                  }
                  label={selectAll.label}
                />
              </Grid>
            )}
            {!selectAll?.value ? (
              <>
                {onSearch && (
                  <Grid container>
                    {marginSearchTop && <br />}
                    <SearchBar
                      id="search"
                      isLoading={isLoading}
                      searchValue={searchValue}
                      handleSearch={onSearch}
                      setSearchValue={setSearchValue}
                    />
                  </Grid>
                )}

                <Grid container className={classes.content}>
                  {children}
                </Grid>
              </>
            ) : (
              <Grid item xs={12}>
                <Alert severity="info">
                  {i18n.t('INFO_AUTOMATICALLY_ADDED_KEY', {
                    more: i18n.t(`NEW_${slug}_KEY`),
                  })}
                </Alert>
              </Grid>
            )}
          </>
        )}
      </Grid>

      <DialogAlert {...alertData} />
    </React.Fragment>
  );
};

const mapStateToProps = ({
  authenticate: { data, companySelected },
  permission: { roleInfo },
}: ApplicationState) => ({
  companySelected,
  role: data?.role,
  roleInfo,
});

export default connect(mapStateToProps)(TemplateGroups);
